import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from 'react-avatar';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import ElipsisText from '../../../../../components/ElipsisText';
import ElipsisCell from '../../../../../components/TableBox/Cells/EllipsisCell';
import Link from '../../../../../components/Link';
import Badge from '../../../../../components/Badge';
import Button from '../../../../../components/Button';
import {
  RowClassName,
  PermissionModulesEnum,
  PermissionActionsEnum,
} from '../../../../../enums';
import { TableContext } from '../../../../../contexts/TableContext';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { hasResourseAccess } from '../../../../../utils/methods';
import { StockistProductResponse } from '../../../../../interfaces/stockist.interface';

interface RowProps {
  item: StockistProductResponse;
  className?: RowClassName;
}

const ProductRow: React.FC<RowProps> = ({ item, className }) => {
  const { t } = useTranslation();
  const { headCells, rowProps } = useContext(TableContext);
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.Stockists]: [PermissionActionsEnum.Update] }],
  });

  const handleEditButtonClick = (id: string | number) => {
    if (rowProps?.onEdit) {
      rowProps.onEdit(id);
    }
  };

  const handleViewClick = (id: string | number) => {
    if (rowProps?.onEdit) {
      rowProps.onEdit(id);
    }
  };

  const nameNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 42 }}>
        <Avatar name={item.name || '-'} size="40" maxInitials={2} round />
      </div>
      <div style={{ width: 'calc(100% - 52px)' }}>
        <ElipsisText
          width="100%"
          text={item.name || '-'}
          node={<Link text={item.name || '-'} />}
          className="h6 mb-1 text-decoration-underline hyperlink"
          onClick={() => handleViewClick(item.id)}
        />
      </div>
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.name) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={nameNode} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.description) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.description || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={
                  item.is_active
                    ? t('stockists.stockistProduct.list.pageTexts.table.status.active') || 'Active'
                    : t('stockists.stockistProduct.list.pageTexts.table.status.inActive') || 'Inactive'
                }
                variant={item.is_active ? 'success' : 'danger'}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-sm-center justify-content-sm-start">
                <Button
                  className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
                  icon={<i className={`ri-${hasEditAccess ? 'edit-box' : 'eye'}-line ri-20px`}></i>}
                  onClick={() => handleEditButtonClick(item.id)}
                />
              </div>
            </td>
          );
        }

        const value = item[headCell.id as keyof StockistProductResponse];
        return (
          <ElipsisCell key={headCell.id} width={headCell.width} text={value ? String(value) : '-'} />
        );
      })}
    </tr>
  );
};

export default ProductRow;
